import React, { useCallback } from 'react';
import _ from 'lodash';
import { Flex, Spinner } from '@patternfly/react-core';
import { DateTime } from 'luxon';

import TelemetryCardList from 'components/TelemetryCard/TelemetryCardList';
import T from 'localization/LocalizationKeys';
import { useTranslation } from 'localization/useTranslation';
import Icons, { Size } from 'components/Common/Icons';
import useInterval from 'utils/hooks/useInterval';
import ModelType from 'components/Model/ModelType';
import dataSources from 'components/Dashboard/datasources';
import { SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import { sortLocale } from 'utils/string';
import {
  getLastValueAssets
} from 'components/Dashboard/panels/TelemetryCardsPanel/LastValueTelemetryCard';

import './TelemetryCardsPanel.scss';

const DEFAULT_REFRESH_INTERVAL = 5; // 5 minutes
const MIN_REFRESH_INTERVAL = 1; // 1 minute

export const SELECTABLE_DATA_SOURCES = Object.freeze(_.map(_.pick(dataSources, ['realTimeTelemetry', 'lastValue']), ({ label }, type) => ({ type, label })));
const GROUP_BY = Object.freeze([
  {
    value: 'bySignal',
    text: T.common.groupBy.signal
  },
  {
    value: 'byAsset',
    text: T.common.groupBy.asset
  }
]);
const DEFAULT_GROUP_BY = _.find(GROUP_BY, { value: 'byAsset' });

const SORT_BY = Object.freeze([
  {
    value: SortDirection.ASC,
    text: T.common.sortBy.ascending
  },
  {
    value: SortDirection.DESC,
    text: T.common.sortBy.descending
  }
]);
const DEFAULT_SORT_BY = _.find(SORT_BY, { value: SortDirection.ASC });

// Here we select the default value for dataSource property
// TODO: use const for lastValue, or maybe use a default: true in the list of selectable data source.
export const DEFAULT_DATA_SOURCE = _.find(SELECTABLE_DATA_SOURCES, { type: 'lastValue' });

const TelemetryCardsPanel = ({ data, api }) => {


  const { refreshInterval, hasError, isLoading } = data.signalValues;

  useInterval(api.reload, Math.max(MIN_REFRESH_INTERVAL, (refreshInterval ?? DEFAULT_REFRESH_INTERVAL) * 60000));

  const T = useTranslation();
  const loadingDescription = hasError ? T.common.error.generic : T.common.loading;

  const sortItems = useCallback((items, key = 'displayName') => {
    const result = sortLocale(_.uniqBy(items, key), key);
    return (data?.sortBy === SortDirection.DESC) ? _.reverse(result) : result;
  }, [data?.sortBy]);

  const groupByAsset = useCallback((assetValues, isLoading) => {
    const sortedAssets = sortItems(assetValues, 'assetDisplayName');
    return (_.map(sortedAssets, asset => {
      return (<TelemetryCardList
        key={asset.assetId}
        assetId={asset.assetId}
        header={asset.assetDisplayName}
        telemetryModelValues={sortItems(asset.schemas)}
        isLoading={isLoading}
        hideChart
      />);
    }));
  }, [sortItems]);

  const groupBySignal = useCallback((assetValues, isLoading) => {
    const schemas = _.flatten(_.map(assetValues, asset => {
      return _.map(asset.schemas, schema => {
        return {
          ...schema,
          assetDisplayName: asset.assetDisplayName
        };
      });
    }));
    const sortedSignals = _.map(sortItems(schemas, 'displayName'), 'name');
    const fixed = _.map(sortedSignals, signal => {
      const cards = _.filter(schemas, schema => schema.name === signal);
      return _.map(cards, card => {
        return {
          ...card,
          displayName: card.assetDisplayName,
          signalName: card.displayName
        };
      });
    });
    return (_.map(fixed, signalList => {
      return (<TelemetryCardList
        assetId={null}
        header={signalList[0].signalName}
        telemetryModelValues={sortItems(signalList)}
        isLoading={isLoading}
        hideChart
      />);
    }));
  }, [sortItems]);

  const renderGroupedTelemetry = useCallback((values, groupBy) => {
    if (_.isEmpty(values)) {
      return (
        <></>
      );
    }
    switch (groupBy) {
      case 'byAsset':
        return groupByAsset(values, isLoading);
      case 'bySignal':
        return groupBySignal(values, isLoading);
      default:
        return groupByAsset(values, isLoading);
    }
  }, [groupByAsset, groupBySignal, isLoading]);

  const mapSignalDataToAssetValues = useCallback((signalData) => {
    const assetIds = _.uniq(_.map(signalData, 'assetId'));
    const assetValues = _.map(assetIds, assetId => {
      const assetSchemas = _.filter(signalData, { assetId });
      return {
        assetId,
        assetDisplayName: _.first(assetSchemas)?.assetDisplayName,
        schemas: _.map(assetSchemas, schema => ({
          name: schema.signalId,
          displayName: schema.signalDisplayName,
          type: schema.type,
          unit: schema.unit,
          componentName: schema.componentName,
          value: {
            name: schema.signalId,
            componentName: schema.componentName,
            timestamp: (!_.isEmpty(schema.dataPoints))
              ? DateTime.fromMillis(_.first(schema.dataPoints)?.[0]).toISO()
              : '',
            value: _.first(schema.dataPoints)?.[1] ?? ''
          }
        }))
      };
    });
    return assetValues;
  }, []);

  const renderBySource = () => {
    switch (data.dataSource) {
      case 'realTimeTelemetry':
        const values = mapSignalDataToAssetValues(data.signalValues?.signalData);
        return renderGroupedTelemetry(values, data?.groupBy);// return lastValueTelemetry();
      case 'lastValue':
        return renderGroupedTelemetry(getLastValueAssets(data.signalValues), data?.groupBy);
      default:
        return renderGroupedTelemetry(getLastValueAssets(data.signalValues), data?.groupBy);
    }
  };

  // console.log('data', data?.signalValues?.signalData);
  return (
    <div className={'telemetrycard-panel-container'}>
      { (isLoading || hasError) && <Flex alignItems={{ default: 'alignItemsCenter' }}>
        <Spinner size="md" />
        <span>{ loadingDescription }</span>
      </Flex> }
      { renderBySource() }
    </div>
  );
};

TelemetryCardsPanel.models = [
  {
    path: 'dataSource',
    label: T.common.dataSource,
    type: ModelType.OPTIONSV2,
    options: _.map(SELECTABLE_DATA_SOURCES, ({ type, label }) => ({ text: label, value: type })),
    placeholder: DEFAULT_DATA_SOURCE.label
  },
  {
    path: 'sortBy',
    label: T.common.sortBy.label,
    type: ModelType.OPTIONSV2,
    options: SORT_BY,
    placeholder: DEFAULT_SORT_BY.text
  },
  {
    path: 'groupBy',
    label: T.common.groupBy.label,
    type: ModelType.OPTIONSV2,
    options: GROUP_BY,
    placeholder: DEFAULT_GROUP_BY.text
  }
];

TelemetryCardsPanel.info = {
  label: T.dashboard.panels.types.telemetryCard,
  icon: <Icons.Telemetry size={Size.LARGE}/>,
  help: 'panels/telemetrycards'
};

TelemetryCardsPanel.properties = panelProperties => {
  const modifiedProps = {
    signalValues: {
      type: panelProperties?.dataSource ?? DEFAULT_DATA_SOURCE.type
    }
  };
  // const modifiedProps =_.merge({}, panelProperties, signalValues);
  return modifiedProps;
};
export default React.memo(TelemetryCardsPanel);
